'use client'

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Minus, Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  applyAnchorZoom,
  applyPinch,
  clamp,
  clampTranslate,
  computeFitScale,
  distance,
  DOUBLE_TAP_MOVE,
  DOUBLE_TAP_MS,
  DOUBLE_TAP_SCALE_FACTOR,
  MAX_SCALE,
  midpoint,
  WHEEL_FACTOR,
  type Transform,
} from './lightbox-math'
import './lightbox.css'

/** The single image shown by the overlay. */
export interface LightboxItem {
  src: string
  alt: string
}

interface LightboxProps {
  /** Non-null opens the overlay; null keeps it unmounted. */
  item: LightboxItem | null
  /** Called when the user dismisses (×, Esc, or backdrop tap). */
  onClose: () => void
}

/** Keyboard/button zoom step (a touch larger than a single wheel notch). */
const BUTTON_ZOOM_STEP = 1.3

const FIT_TRANSFORM: Transform = { scale: 1, tx: 0, ty: 0 }

/**
 * Full-screen zoom/pan overlay rendered into `document.body` via a portal.
 *
 * Owns all pointer / wheel / keyboard interaction; the pure geometry lives
 * in `lightbox-math.ts`. Mounted lazily (only while `item` is set) so no
 * listeners or DOM exist when closed. Used by both `LightboxProvider`
 * (one shared instance app-wide) and the standalone `ZoomableImage`
 * fallback, so it must stay self-contained.
 */
export function Lightbox({ item, onClose }: LightboxProps) {
  const { t } = useTranslation(['web'])
  const [mounted, setMounted] = useState(false)
  const [nat, setNat] = useState({ w: 0, h: 0 })
  const [view, setView] = useState<Transform>(FIT_TRANSFORM)
  const [coarsePointer, setCoarsePointer] = useState(false)

  const overlayRef = useRef<HTMLDivElement | null>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  // Gesture bookkeeping kept in refs so pointer/wheel handlers always read
  // the latest values without re-subscribing on every render.
  const viewRef = useRef<Transform>(view)
  const natRef = useRef(nat)
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const gesture = useRef({ lastX: 0, lastY: 0, downX: 0, downY: 0, lastDist: 0, lastMidX: 0, lastMidY: 0 })
  const cycle = useRef({ moved: false, pinched: false })
  const lastTap = useRef({ t: 0, x: 0, y: 0 })

  // NB: do NOT mirror `viewRef.current = view` on every render. During a
  // drag/pinch `commit(..., false)` advances viewRef without setView, so the
  // declarative `view` lags; re-mirroring here would clobber the live gesture
  // value on any incidental re-render. viewRef is owned by commit; natRef by
  // handleImgLoad. Both are seeded from the initial useRef(state) above.

  useEffect(() => setMounted(true), [])

  const viewport = () => ({
    vw: typeof window === 'undefined' ? 0 : window.innerWidth,
    vh: typeof window === 'undefined' ? 0 : window.innerHeight,
  })

  const currentFit = useCallback(() => {
    const { vw, vh } = viewport()
    return computeFitScale({ natW: natRef.current.w, natH: natRef.current.h, vw, vh })
  }, [])

  /**
   * Clamp the translation and apply a new transform.
   *
   * The visible transform is written straight to the <img> (GPU translate +
   * scale) so a drag/pinch updates at input rate with no React reconcile.
   * `render` syncs the declarative `view` state too — pass false on the hot
   * per-frame gesture paths and flush once when the gesture ends; the
   * low-frequency paths (load / reset / wheel / keyboard / resize) pass true.
   */
  const commit = useCallback((next: Transform, render = true) => {
    const { vw, vh } = viewport()
    const clamped = clampTranslate({
      tx: next.tx,
      ty: next.ty,
      scale: next.scale,
      natW: natRef.current.w,
      natH: natRef.current.h,
      vw,
      vh,
    })
    const resolved: Transform = { scale: next.scale, tx: clamped.tx, ty: clamped.ty }
    viewRef.current = resolved
    const el = imgRef.current
    if (el) el.style.transform = `translate(${resolved.tx}px, ${resolved.ty}px) scale(${resolved.scale})`
    if (render) setView(resolved)
  }, [])

  /** Zoom toward a viewport anchor, clamped to [fit, MAX_SCALE]. */
  const zoomTo = useCallback(
    (nextScale: number, cx: number, cy: number) => {
      const next = clamp(nextScale, currentFit(), MAX_SCALE)
      commit(applyAnchorZoom({ ...viewRef.current, next, cx, cy }))
    },
    [commit, currentFit]
  )

  /** Reset to the fitted, centred view. */
  const resetToFit = useCallback(() => {
    commit({ scale: currentFit(), tx: 0, ty: 0 })
  }, [commit, currentFit])

  /** +/- buttons and keyboard zoom about the viewport centre. */
  const zoomByStep = useCallback(
    (factor: number) => {
      const { vw, vh } = viewport()
      zoomTo(viewRef.current.scale * factor, vw / 2, vh / 2)
    },
    [zoomTo]
  )

  // Fit the image once its natural size is known.
  const handleImgLoad = useCallback(() => {
    const el = imgRef.current
    if (!el) return
    const w = el.naturalWidth
    const h = el.naturalHeight
    natRef.current = { w, h }
    setNat({ w, h })
    const fit = computeFitScale({ natW: w, natH: h, ...viewport() })
    commit({ scale: fit, tx: 0, ty: 0 })
  }, [commit])

  // Detect coarse (touch) pointers to drop the "scroll" hint on mobile.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    setCoarsePointer(window.matchMedia('(pointer: coarse)').matches)
  }, [])

  // Lock background scroll + manage focus for the lifetime of the overlay.
  useEffect(() => {
    if (!item || typeof document === 'undefined') return
    returnFocusRef.current = document.activeElement as HTMLElement | null
    const body = document.body
    const prevOverflow = body.style.overflow
    body.style.overflow = 'hidden'
    // Move focus to the close button (next paint so it exists & is laid out).
    const raf = window.requestAnimationFrame(() => closeBtnRef.current?.focus())
    return () => {
      window.cancelAnimationFrame(raf)
      body.style.overflow = prevOverflow
      returnFocusRef.current?.focus?.()
    }
  }, [item])

  // Reset to the fitted view every time the overlay (re)opens. The provider
  // reuses one mounted instance, so without this, reopening the SAME image
  // (whose <img onLoad> won't refire) would show the previous zoom/pan. For a
  // different image, handleImgLoad re-fits once it decodes.
  useEffect(() => {
    if (!item) return
    // Clear per-session gesture state so a tap that closed a previous session
    // can't be read as the first half of a double-tap on reopen.
    lastTap.current = { t: 0, x: 0, y: 0 }
    pointers.current.clear()
    cycle.current = { moved: false, pinched: false }
    commit({ scale: currentFit(), tx: 0, ty: 0 })
  }, [item, commit, currentFit])

  // Assert the transform from viewRef on every render (synchronously, pre-paint)
  // so the imperative gesture writes survive any incidental re-render — the
  // declarative `view` can lag during a drag/pinch and must never repaint stale.
  useLayoutEffect(() => {
    const el = imgRef.current
    if (el) {
      const { tx, ty, scale } = viewRef.current
      el.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`
    }
  })

  // Non-passive wheel + window resize listeners (React's onWheel is passive,
  // so it cannot preventDefault the page zoom/scroll).
  useEffect(() => {
    if (!item) return
    const overlay = overlayRef.current
    if (!overlay) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const factor = e.deltaY < 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR
      zoomTo(viewRef.current.scale * factor, e.clientX, e.clientY)
    }
    const onResize = () => {
      const fit = currentFit()
      commit({ ...viewRef.current, scale: clamp(viewRef.current.scale, fit, MAX_SCALE) })
    }
    overlay.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('resize', onResize)
    return () => {
      overlay.removeEventListener('wheel', onWheel)
      window.removeEventListener('resize', onResize)
    }
  }, [item, zoomTo, commit, currentFit])

  // Keyboard: Esc closes, +/- zoom, with a focus trap across the controls.
  useEffect(() => {
    if (!item) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        zoomByStep(BUTTON_ZOOM_STEP)
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        zoomByStep(1 / BUTTON_ZOOM_STEP)
      } else if (e.key === 'Tab') {
        const focusables = overlayRef.current?.querySelectorAll<HTMLElement>('button')
        if (!focusables || focusables.length === 0) return
        const list = Array.from(focusables)
        const idx = list.indexOf(document.activeElement as HTMLElement)
        e.preventDefault()
        const nextIdx = e.shiftKey
          ? (idx <= 0 ? list.length - 1 : idx - 1)
          : (idx === list.length - 1 ? 0 : idx + 1)
        list[nextIdx]?.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [item, onClose, zoomByStep])

  const isControl = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest('[data-lb-control]')

  const onPointerDown = (e: React.PointerEvent) => {
    if (isControl(e.target)) return
    overlayRef.current?.setPointerCapture?.(e.pointerId)
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 1) {
      cycle.current = { moved: false, pinched: false }
      gesture.current.lastX = e.clientX
      gesture.current.lastY = e.clientY
      gesture.current.downX = e.clientX
      gesture.current.downY = e.clientY
    } else if (pointers.current.size === 2) {
      cycle.current.pinched = true
      const [a, b] = Array.from(pointers.current.values())
      gesture.current.lastDist = distance(a.x, a.y, b.x, b.y)
      const mid = midpoint(a.x, a.y, b.x, b.y)
      gesture.current.lastMidX = mid.x
      gesture.current.lastMidY = mid.y
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (pointers.current.size >= 2) {
      // Pinch: scale by the finger-distance ratio while the pinch centre
      // follows the fingers. applyPinch anchors the zoom at the PREVIOUS
      // midpoint (so the content under it stays put across the scale change)
      // then pans by the midpoint delta — anchoring at the live midpoint here
      // would double-count the finger travel and the image would slip.
      const [a, b] = Array.from(pointers.current.values())
      const dist = distance(a.x, a.y, b.x, b.y)
      const mid = midpoint(a.x, a.y, b.x, b.y)
      if (gesture.current.lastDist > 0) {
        commit(
          applyPinch({
            ...viewRef.current,
            lastDist: gesture.current.lastDist,
            dist,
            lastMid: { x: gesture.current.lastMidX, y: gesture.current.lastMidY },
            mid,
            fit: currentFit(),
          }),
          false
        )
      }
      gesture.current.lastDist = dist
      gesture.current.lastMidX = mid.x
      gesture.current.lastMidY = mid.y
      return
    }

    // Single pointer: drag-pan. `moved` tracks CUMULATIVE travel from the
    // press point (not per-frame delta) so a slow drag still suppresses the
    // tap-to-close / double-tap that would otherwise fire on release.
    const dx = e.clientX - gesture.current.lastX
    const dy = e.clientY - gesture.current.lastY
    gesture.current.lastX = e.clientX
    gesture.current.lastY = e.clientY
    if (distance(gesture.current.downX, gesture.current.downY, e.clientX, e.clientY) > DOUBLE_TAP_MOVE) {
      cycle.current.moved = true
    }
    commit(
      { scale: viewRef.current.scale, tx: viewRef.current.tx + dx, ty: viewRef.current.ty + dy },
      false
    )
  }

  /** True when the viewport point falls outside the rendered image rect. */
  const isOutsideImage = (x: number, y: number) => {
    // Natural size unknown (pre-load / decode failure): treat every point as
    // ON the image so a tap during loading can't accidentally dismiss.
    if (natRef.current.w === 0 || natRef.current.h === 0) return false
    const { scale, tx, ty } = viewRef.current
    const left = tx
    const top = ty
    const right = tx + natRef.current.w * scale
    const bottom = ty + natRef.current.h * scale
    return x < left || x > right || y < top || y > bottom
  }

  const onPointerUp = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return
    overlayRef.current?.releasePointerCapture?.(e.pointerId)
    pointers.current.delete(e.pointerId)

    if (pointers.current.size >= 2) {
      // Still pinching (e.g. 3→2 fingers): re-seed the pinch baseline from the
      // remaining two, else the next move reads a stale dist/mid and jumps.
      const [a, b] = Array.from(pointers.current.values())
      gesture.current.lastDist = distance(a.x, a.y, b.x, b.y)
      const mid = midpoint(a.x, a.y, b.x, b.y)
      gesture.current.lastMidX = mid.x
      gesture.current.lastMidY = mid.y
      return
    }
    if (pointers.current.size === 1) {
      // Pinch → drag: re-baseline the remaining finger.
      const [p] = Array.from(pointers.current.values())
      gesture.current.lastX = p.x
      gesture.current.lastY = p.y
      return
    }

    // All fingers up. Ignore taps that were drags or part of a pinch — but sync
    // the declarative state once (the hot path skipped setView) and clear the
    // tap timer so the gesture can't be misread as half of a double-tap.
    if (cycle.current.moved || cycle.current.pinched) {
      lastTap.current = { t: 0, x: 0, y: 0 }
      setView(viewRef.current)
      return
    }

    const now = Date.now()
    const isDoubleTap =
      now - lastTap.current.t < DOUBLE_TAP_MS &&
      distance(lastTap.current.x, lastTap.current.y, e.clientX, e.clientY) < DOUBLE_TAP_MOVE
    if (isDoubleTap) {
      lastTap.current = { t: 0, x: 0, y: 0 }
      const fit = currentFit()
      const target = viewRef.current.scale > fit * 1.05 ? fit : fit * DOUBLE_TAP_SCALE_FACTOR
      zoomTo(target, e.clientX, e.clientY)
      return
    }
    lastTap.current = { t: now, x: e.clientX, y: e.clientY }
    // A single, non-moved tap on empty backdrop dismisses.
    if (isOutsideImage(e.clientX, e.clientY)) onClose()
  }

  if (!mounted || !item || typeof document === 'undefined') return null

  const isZoomed = view.scale > currentFit() * 1.05
  const hintText = coarsePointer ? t('web:zoomImage.hintTouch') : t('web:zoomImage.hint')

  return createPortal(
    <div
      ref={overlayRef}
      className="kiao-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={item.alt || t('web:zoomImage.dialogLabel')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <img
        ref={imgRef}
        src={item.src}
        alt={item.alt}
        className="kiao-lightbox-img"
        draggable={false}
        onLoad={handleImgLoad}
        style={{
          width: nat.w ? `${nat.w}px` : 'auto',
          height: nat.h ? `${nat.h}px` : 'auto',
          // transform is asserted imperatively from viewRef (see the
          // useLayoutEffect) so a mid-gesture re-render can't paint a stale view.
          cursor: isZoomed ? 'grab' : 'default',
        }}
      />

      <button
        ref={closeBtnRef}
        type="button"
        data-lb-control
        className="kiao-lightbox-close"
        aria-label={t('web:zoomImage.close')}
        onClick={onClose}
      >
        <X size={20} strokeWidth={2} aria-hidden="true" />
      </button>

      <div data-lb-control className="kiao-lightbox-controls">
        <button
          type="button"
          className="kiao-lightbox-btn"
          aria-label={t('web:zoomImage.zoomOut')}
          onClick={() => zoomByStep(1 / BUTTON_ZOOM_STEP)}
        >
          <Minus size={20} strokeWidth={2} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="kiao-lightbox-btn kiao-lightbox-fit"
          aria-label={t('web:zoomImage.fit')}
          onClick={resetToFit}
        >
          {/* "FIT" is an intentional locale-neutral control glyph (per the
              −/FIT/+ control spec); the localized meaning rides on aria-label. */}
          FIT
        </button>
        <button
          type="button"
          className="kiao-lightbox-btn"
          aria-label={t('web:zoomImage.zoomIn')}
          onClick={() => zoomByStep(BUTTON_ZOOM_STEP)}
        >
          <Plus size={20} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      <p className="kiao-lightbox-hint" aria-hidden="true">
        {hintText}
      </p>
    </div>,
    document.body
  )
}
